import { type VariantProp, type VariantProps, cv, cx } from "@suse-o11y/class-variance";
import { mergeRefs } from "@suse-o11y/merge-refs";
import type { Record } from "@suse-o11y/record";
import { useComputed } from "@suse-o11y/signal";
import { useOnInvalidFormFocus } from "@suse-o11y/signal/form";
import { Select, type SelectProps } from "antd/es";
import type { RefSelectProps } from "antd/es/select";
import React from "react";

import { useFormFieldContext } from "~/components/form/use-form-field-context";

import styles from "./_multi-select.module.css";

const variants = cv(styles.multiSelect, {
	variants: {
		variant: {
			neutral: styles.neutral,
			error: styles.error,
		},
		size: {
			sm: styles.sm,
			md: styles.md,
			lg: styles.lg,
		},
	},
	defaultVariants: {
		size: "md",
		variant: "neutral",
	},
});

const SIZE_TO_ANTD_SIZE = {
	sm: "small",
	md: "middle",
	lg: "large",
} as const satisfies Record<VariantProp<typeof variants, "size">>;

export interface MultiSelectProps
	extends VariantProps<typeof variants>,
		Omit<
			SelectProps<ReadonlyArray<string>>,
			"value" | "defaultValue" | "searchValue" | "mode" | "size" | "onChange" | "onSearch" | "onSelect" | "onDeselect"
		> {
	loading?: boolean;
	values?: ReadonlyArray<string>;
	onValuesChange?: (values: Array<string>) => void;

	search?: string;
	onSearchChange?: (search: string) => void;

	onSelect?: (value: string) => void;
	onDeselect?: (value: string) => void;
}

export const MultiSelect = React.forwardRef<RefSelectProps, MultiSelectProps>(
	(
		{ className, loading, disabled, variant, size, search, values, onValuesChange, onBlur, onSearchChange, ...props },
		ref,
	) => {
		const formField = useFormFieldContext();
		const focusRef = useOnInvalidFormFocus<RefSelectProps>(formField?.state);
		const submitting = useComputed((monitor) => formField?.state.isSubmitting(monitor), [formField?.state]);
		const invalid = useComputed((monitor) => formField?.state.isInvalid(monitor), [formField?.state]);
		const busy = loading || submitting;

		return (
			<Select<Array<string>>
				id={formField?.id}
				ref={mergeRefs([ref, focusRef])}
				{...props}
				aria-busy={busy}
				className={variants({ className, variant: invalid ? "error" : variant, size })}
				disabled={busy || disabled}
				dropdownClassName={cx("pointer-events-auto")}
				mode="multiple"
				onBlur={(event) => {
					formField?.state.setTouched(true);
					onBlur?.(event);
				}}
				onChange={onValuesChange}
				onSearch={onSearchChange}
				searchValue={search}
				size={size == null ? undefined : SIZE_TO_ANTD_SIZE[size]}
				value={values as undefined | Array<string>}
			/>
		);
	},
);
